import { GetterTree } from 'vuex';
import { RootState } from '@/store';
import { State } from './state';
import { Opportunity } from '@/types/Opportunities';
import { map, get, forEach } from 'lodash';
import { formatDataValue } from '@/helpers/format';
import { FormatOptions, KeyValuePair } from '@/types/Global';
import { Field, FieldValidation } from '@/types/Field';
import {
  OpportunityResponse,
  GetCalculatedOpportunitiesDataItem,
  OpportunityFieldResponse,
  GetSaveOpportunitiesDataItem,
} from './types';
import { DataType } from '@/enums/Global';
import {
  arrayToKeyValue,
  fieldTableGenerator,
  fieldTableGeneratorV2,
} from '@/helpers/opportunities';
import { useI18n } from 'vue-i18n';
import { Task } from '@/types/Task';

export type Getters = {
  getTasksLoading(state: State): boolean;
  getTasks(state: State): Array<Task>;
  clientHasTasks(state: State): boolean;
  getOpportunities(state: State, taskId: string): Array<Opportunity>;
  getOpportunityNames(state: State): Array<string>;
  getOpportunityToCalculate(
    state: State,
    taskId: string
  ): GetCalculatedOpportunitiesDataItem | null;
  getOpportunityToSave(
    state: State,
    taskId: string
  ): GetSaveOpportunitiesDataItem | null;
  getIsLoading(state: State): boolean;
  getIsModalLoading(state: State): boolean;
  getIsModalSubmitting(state: State): boolean;
  getPotentialOpList(state: State): Array<KeyValuePair>;
  getPotentialOp(
    state: State,
    oppName: string
  ): OpportunityResponse | undefined;
  getLastMadeTaskId(state: State): string | number | null;
};

const _checkOpportunityIsValid = (opportunity: OpportunityResponse) => {
  if (opportunity) {
    let isEditableFieldCount = 0;
    let hasValueCount = 0;
    forEach(opportunity.fields, field => {
      if (field.isEditable.dataValue && !field.isOptional.dataValue) {
        isEditableFieldCount++;
        if (field.value.dataValue !== null && field.value.dataValue !== '') {
          hasValueCount++;
        }
      }
    });
    if (isEditableFieldCount === hasValueCount) {
      return true;
    }
  }
  return false;
};

const _formatOpportunityFields = (fields: Array<OpportunityFieldResponse>) => {
  return map(fields, field => {
    return {
      isEditable: formatDataValue(field.isEditable),
      isOptional: formatDataValue(field.isOptional),
      fieldType: formatDataValue(field.fieldType),
      priority: formatDataValue(field.priority, {
        keepZeroAsZero: true,
      } as FormatOptions),
      name: formatDataValue(field.name),
      value: formatDataValue(field.value, {
        bypassFormatting: true,
      } as FormatOptions),
      valueType: get(field, 'value.dataType', 'string'),
      validation: get(field, 'validation', {} as FieldValidation),
      warning: formatDataValue(field.warning),
      section: field.section.dataValue ? field.section.dataValue : 0,
      grouping: field.grouping.dataValue ? field.grouping.dataValue : 0,
    } as Field;
  });
};

const _createOpportunityObject = (
  opp: OpportunityResponse,
  columnKeys: Array<string>,
  useExternalApprovalSystem: boolean
) => {
  const opportunity = {
    category: formatDataValue(opp.category),
    reference: formatDataValue(opp.opportunityReference),
    name: formatDataValue(opp.opportunityName),
    isValid: _checkOpportunityIsValid(opp),
    fields: _formatOpportunityFields(opp.fields),
    fieldTable: fieldTableGenerator(columnKeys, opp),
    fieldTableV2: fieldTableGeneratorV2(opp),
    passesValidation: formatDataValue(opp.passesValidation),
  };

  if (!useExternalApprovalSystem) {
    ['dueDate', 'comment', 'confidenceLevel'].forEach(key => {
      const fieldValue = get(opp, `${key}.dataValue`, null);
      if (fieldValue) {
        opportunity[key] = fieldValue;
      }

      opportunity[key] = formatDataValue(
        get(opp, key, { dataValue: '', dataType: DataType.STRING }),
        {
          bypassFormatting: true,
        } as FormatOptions
      );
    });
  }

  opportunity['drivers'] = {};
  for (const [key, value] of Object.entries(opp.opportunityDrivers)) {
    if (['driver_1', 'driver_2', 'driver_3'].includes(key.toLowerCase())) {
      opportunity['drivers'][key.toLowerCase()] = value;
    }
  }
  return opportunity as Opportunity;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getTasksLoading: state => state.tasksLoading,
  getTasks: state => state.tasks,
  clientHasTasks: state => !!state.tasks.length,
  getOpportunities: (state, taskId) => {
    const oppState = state.opportunities[taskId];
    if (!oppState) return [];
    return [_createOpportunityObject(oppState, ['fields'], false)];
  },
  getOpportunityNames: state => {
    return state.tasks.map(task => task.opportunityName);
  },
  getOpportunityToCalculate: (state, taskId) => {
    const opportunity = state.opportunities[taskId];

    if (opportunity) {
      const opportunityReference = opportunity.opportunityReference
        .dataValue as string;

      return {
        opportunityReference: opportunityReference,
        calculationInput: arrayToKeyValue(opportunity.fields),
      } as GetCalculatedOpportunitiesDataItem;
    }

    return null;
  },
  getOpportunityToSave: (state, taskId) => {
    const opportunity = state.opportunities[taskId];

    if (opportunity) {
      return {
        opportunityId: opportunity.opportunityId.dataValue,
        opportunityReference: opportunity.opportunityReference.dataValue,
        identifiedData: arrayToKeyValue(opportunity.fields),
      } as GetSaveOpportunitiesDataItem;
    }

    return null;
  },
  getIsLoading: state => {
    return state.isLoading;
  },
  getIsModalLoading: state => {
    return state.isModalLoading;
  },
  getIsModalSubmitting: state => {
    return state.isModalSubmitting;
  },
  getPotentialOpList: state => {
    const { t } = useI18n();
    return state.potentialOps.map(op => {
      return {
        key: t(
          `opportunities.opportunity-names.${formatDataValue(
            op.opportunityName
          )}`
        ),
        value: formatDataValue(op.opportunityName),
      };
    });
  },
  getPotentialOp: (state, oppName) => {
    return state.potentialOps.find(
      op => op.opportunityName.dataValue === oppName
    );
  },
  getLastMadeTaskId: state => {
    return state.lastMadeTaskId;
  },
};
